import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    inject,
    OnInit,
    signal,
    WritableSignal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Roles } from '@shs-digi-care-monorepo/interfaces';
import { ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { updateDuplicateEmailErrors } from '../referral-form.utils';
import { AssigneeValue } from '../../sh-assignee-picker/model/sh-assignee-picker.model';
import { FileUploadItem } from '../../sh-file-input/sh-file-input.component';
import { ShSelectComponent } from '../../sh-select/sh-select.component';

export type {
    ParallelReferralDialogData,
    ParallelReferralEntry,
    ParallelReferralDialogResult,
    ReferralFormGroup
} from '../model/parallel-referral-dialog.model';
import type {
    ParallelReferralDialogData,
    ParallelReferralDialogResult,
    ParallelReferralEntry,
    ReferralFormGroup
} from '../model/parallel-referral-dialog.model';
import { ReferralTabComponent } from '../referral-tab/referral-tab.component';

@Component({
    selector: 'sh-parallel-referral-dialog',
    templateUrl: './parallel-referral-dialog.component.html',
    imports: [TranslateModule, ReactiveFormsModule, ReferralTabComponent, ShSelectComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ParallelReferralDialog implements OnInit {
    private readonly dialogRef = inject<MatDialogRef<ParallelReferralDialog>>(MatDialogRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);
    public data = inject<ParallelReferralDialogData>(MAT_DIALOG_DATA);

    protected readonly MAX_REFERRALS = 3;
    protected readonly MIN_REFERRALS = 1;
    protected readonly rolesEnum = Roles;

    protected forms: WritableSignal<FormGroup<ReferralFormGroup>[]> = signal([]);
    protected activeTabIndex: WritableSignal<number> = signal(0);
    private formStatusTick: WritableSignal<number> = signal(0);

    protected maxReferrals = computed(() => {
        return this.data.supportsParallelReferral ? this.MAX_REFERRALS : this.MIN_REFERRALS;
    });

    protected description = computed(() => {
        if (this.data.isExternal) {
            return 'nam.external-referral-dialog.description';
        }
        return this.maxReferrals() === this.MIN_REFERRALS
            ? 'nam.parallel-referral-dialog.description-limit'
            : 'nam.parallel-referral-dialog.description';
    });

    protected isConfirmDisabled = computed(() => {
        this.formStatusTick();
        if (this.forms().some((f) => f.invalid)) {
            return true;
        }
        if (this.hasClassification() && !this.data.isReferredOutAfterSubmission && this.classificationControl.invalid) {
            return true;
        }
        return false;
    });

    protected canAddTab = computed(() => this.forms().length < this.maxReferrals());

    /** Set of assignee IDs/emails already selected in other tabs, keyed by tab index */
    protected excludeIdsPerTab: WritableSignal<Map<number, Set<string>>> = signal(new Map());

    protected readonly hasClassification = computed(() => !!this.data.requestTypeOptions);
    protected readonly requestTypeOptions = computed(() => this.data.requestTypeOptions ?? []);
    protected readonly classificationOptions = computed(() => this.data.classificationOptions?.() ?? []);

    protected readonly requestTypeControl = new FormControl<string | null>(this.data.requestType ?? null);
    protected readonly classificationControl = new FormControl<string | null>(
        this.data.existingClassification ?? null,
        this.data.requestTypeOptions ? [Validators.required] : []
    );

    public ngOnInit(): void {
        this.addReferral();
        if (this.data.onRequestTypeChange) {
            this.requestTypeControl.valueChanges
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    tap((type) => {
                        this.classificationControl.setValue(null);
                        this.data.onRequestTypeChange?.(type);
                    })
                )
                .subscribe();
        }
        this.classificationControl.statusChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => this.formStatusTick.update((v) => v + 1))
            )
            .subscribe();
    }

    protected addReferral(): void {
        if (this.forms().length >= this.maxReferrals()) {
            return;
        }

        const descriptionValidators = this.data.isReferredOutAfterSubmission
            ? []
            : [Validators.required, Validators.pattern(/\S/)];
        const emailValidators = this.data.isExternal ? [Validators.required, Validators.email] : [];
        const assigneeValidators = this.data.isExternal ? [] : [Validators.required];

        const form = new FormGroup<ReferralFormGroup>({
            name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
            assignee: new FormControl<AssigneeValue | null>(null, assigneeValidators),
            referredOutEmail: new FormControl<string>('', { nonNullable: true, validators: emailValidators }),
            description: new FormControl<string | null>(null, descriptionValidators),
            attachments: new FormControl<FileUploadItem[] | null>(null),
            markAsInternal: new FormControl<boolean>(false, { nonNullable: true })
        });

        form.statusChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => this.formStatusTick.update((v) => v + 1))
            )
            .subscribe();

        if (!this.data.isExternal) {
            // Track assignee changes for cross-tab deduplication (internal only)
            form.controls.assignee.valueChanges
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    tap(() => this.rebuildExcludeIds())
                )
                .subscribe();
        }

        if (this.data.isExternal) {
            form.controls.referredOutEmail.valueChanges
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    tap(() => updateDuplicateEmailErrors(this.forms().map((f) => f.controls.referredOutEmail)))
                )
                .subscribe();
        }

        this.forms.update((f) => [...f, form]);
        this.activeTabIndex.set(this.forms().length - 1);
        if (!this.data.isExternal) {
            this.rebuildExcludeIds();
        }
        if (this.data.isExternal) {
            updateDuplicateEmailErrors(this.forms().map((f) => f.controls.referredOutEmail));
        }
    }

    protected removeReferral(index: number): void {
        if (this.forms().length <= this.MIN_REFERRALS) {
            return;
        }
        this.forms.update((f) => f.filter((_, i) => i !== index));
        if (this.activeTabIndex() >= this.forms().length) {
            this.activeTabIndex.set(this.forms().length - 1);
        }
        if (!this.data.isExternal) {
            this.rebuildExcludeIds();
        }
        if (this.data.isExternal) {
            updateDuplicateEmailErrors(this.forms().map((f) => f.controls.referredOutEmail));
        }
    }

    protected setActiveTab(index: number): void {
        this.activeTabIndex.set(index);
    }

    protected getTabLabel(index: number): string {
        const name = this.forms()[index]?.controls.name.value;
        const fallbackKey = 'nam.parallel-referral-dialog.default-tab-title';
        return name || this.translateService.instant(fallbackKey);
    }

    protected getExcludeIds(tabIndex: number): Set<string> {
        return this.excludeIdsPerTab().get(tabIndex) ?? new Set();
    }

    private rebuildExcludeIds(): void {
        const forms = this.forms();
        const newMap = new Map<number, Set<string>>();

        for (let i = 0; i < forms.length; i++) {
            const excluded = new Set<string>();
            for (let j = 0; j < forms.length; j++) {
                if (i === j) {
                    continue;
                }
                const assignee = forms[j].controls.assignee.value;
                if (assignee) {
                    excluded.add(assignee.id);
                    if (assignee.email) {
                        excluded.add(assignee.email);
                    }
                }
            }
            newMap.set(i, excluded);
        }

        this.excludeIdsPerTab.set(newMap);
    }

    public close(): void {
        this.dialogRef.close();
    }

    public confirm(): void {
        if (this.hasClassification() && !this.data.isReferredOutAfterSubmission && this.classificationControl.invalid) {
            this.classificationControl.markAsTouched();
            return;
        }
        const entries: ParallelReferralEntry[] = this.forms().map((form) => ({
            name: form.controls.name.value,
            description: form.controls.description.value,
            assignee: form.controls.assignee.value,
            email: form.controls.referredOutEmail.value || null,
            comment: form.controls.description.value,
            attachments: form.controls.attachments.value,
            markAsInternal: form.controls.markAsInternal.value
        }));
        const result: ParallelReferralDialogResult = {
            entries,
            classification: this.classificationControl.value,
            requestType: this.requestTypeControl.value
        };
        this.dialogRef.close(result);
    }
}
